import React, { useEffect, useMemo, useState } from 'react';
import { Card, Space, Typography, Tag } from 'antd';

import AuthorizedPersonService from '../services/AuthorizedPersonService';
import PhoneNumberDisplay from './PhoneNumberDisplay';
import { notificationTypeLabels, sortNotificationTypes } from '../constants/notificationTypes';

const { Text } = Typography;

type AuthorizedPerson = {
  id?: number | string | null;
  firstName?: string | null;
  lastName?: string | null;
  position?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  notificationTypes?: string[] | null;
};

interface InspectorPlanAuthorizedContactsCardProps {
  companyId?: number | null;
  planAuthorizedPersonIds?: Array<number | string | null> | null;
  loading?: boolean;
  className?: string;
  proposalAuthorizedPersons?: AuthorizedPerson[] | null;
}

const InspectorPlanAuthorizedContactsCard: React.FC<InspectorPlanAuthorizedContactsCardProps> = ({
  companyId,
  planAuthorizedPersonIds,
  loading,
  className,
  proposalAuthorizedPersons,
}) => {
  const [fetching, setFetching] = useState<boolean>(false);
  const [authorizedPersons, setAuthorizedPersons] = useState<AuthorizedPerson[]>([]);

  useEffect(() => {
    let ignore = false;
    if (Array.isArray(proposalAuthorizedPersons)) {
      setAuthorizedPersons(proposalAuthorizedPersons);
      setFetching(false);
      return () => {
        ignore = true;
      };
    }
    if (!companyId) {
      setAuthorizedPersons([]);
      setFetching(false);
      return () => {
        ignore = true;
      };
    }
    const load = async () => {
      try {
        setFetching(true);
        const response = await AuthorizedPersonService.getCompanyAuthorizedPersons(companyId);
        if (ignore) return;
        const records = Array.isArray(response?.data) ? response.data : response?.data?.content;
        setAuthorizedPersons(Array.isArray(records) ? records : []);
      } catch (error) {
        if (!ignore) {
          console.error('InspectorPlanAuthorizedContactsCard: failed to load authorized persons', error);
          setAuthorizedPersons([]);
        }
      } finally {
        if (!ignore) {
          setFetching(false);
        }
      }
    };
    load();
    return () => {
      ignore = true;
    };
  }, [companyId, proposalAuthorizedPersons]);

  const selectedIdSet = useMemo(() => {
    if (!planAuthorizedPersonIds || !planAuthorizedPersonIds.length) {
      return null;
    }
    const ids = planAuthorizedPersonIds
      .map((value) => {
        if (value == null) return null;
        const num = Number(value);
        return Number.isFinite(num) ? num : null;
      })
      .filter((value): value is number => value != null);
    return ids.length ? new Set(ids) : null;
  }, [planAuthorizedPersonIds]);

  const planContacts = useMemo(() => {
    const planEligible = authorizedPersons.filter((person) =>
      Array.isArray(person?.notificationTypes) &&
      person.notificationTypes.some((type) => type?.toUpperCase() === 'PLAN'),
    );
    if (!selectedIdSet) {
      return planEligible;
    }
    return planEligible.filter((person) => {
      const id = typeof person?.id === 'string' ? Number(person.id) : person?.id;
      return typeof id === 'number' && Number.isFinite(id) ? selectedIdSet.has(id) : false;
    });
  }, [authorizedPersons, selectedIdSet]);

  const isLoading = Boolean(loading) || fetching;

  return (
    <Card
      className={className}
      title='Plan Yetkilileri'
      loading={isLoading}
    >
      {isLoading ? null : !companyId ? (
        <Text>Firma bilgisi bulunamadı.</Text>
      ) : planContacts.length === 0 ? (
        <Text>Plan bilgilendirmesi için yetkili kişi bulunamadı.</Text>
      ) : (
        <Space direction='vertical' size={12} style={{ width: '100%' }}>
          {planContacts.map((person) => {
            const firstName = person?.firstName?.trim() || '';
            const lastName = person?.lastName?.trim() || '';
            const fullName = `${firstName} ${lastName}`.trim() || 'İsimsiz Yetkili';
            const position = person?.position?.trim();
            const email = person?.email?.trim();
            const notificationTypes = Array.isArray(person?.notificationTypes)
              ? sortNotificationTypes(person.notificationTypes as any)
              : [];
            return (
              <div
                key={String(person?.id ?? `${fullName}-${position ?? 'position'}`)}
                style={{
                  border: '1px solid #f0f0f0',
                  borderRadius: 12,
                  padding: '12px 16px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <Text strong>{fullName}</Text>
                  <Space size={4} wrap>
                    {notificationTypes
                      .filter((type) => type?.toUpperCase() === 'PLAN')
                      .map((type) => (
                        <Tag color='blue' key={type}>
                          {notificationTypeLabels[type as keyof typeof notificationTypeLabels] ?? type}
                        </Tag>
                      ))}
                  </Space>
                </div>
                {position ? <Text type='secondary'>{position}</Text> : null}
                {email ? <Text>{email}</Text> : null}
                {person?.phoneNumber ? <span><PhoneNumberDisplay value={person.phoneNumber} /></span> : null}
              </div>
            );
          })}
        </Space>
      )}
    </Card>
  );
};

export default InspectorPlanAuthorizedContactsCard;
